import React, { useEffect } from 'react';
import styled from 'styled-components';
import Colours from '../../design-system/colours';
import { useQuery } from '@tanstack/react-query';
import usePaginationQueryParams from '../../hooks/use-pagination-query-params';
import { loadHormonePatternContents } from '../../services/admin/admin-hormone-pattern-content.service';
import queryString from 'query-string';
import { isEmpty } from 'lodash';
import OvPageTitle from '../UI/molecules/OvPageTitle';
import { useTranslation } from 'react-i18next';
import TocIcon from '@mui/icons-material/Toc';
import OvPaginationBar from '../UI/molecules/OvPaginationBar';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvNoContent from '../UI/molecules/OvNoContent';
import OvHormonePatternContentsList from '../UI/organisms/OvHormonePatternContentsList';

export const ADMIN_HORMONE_PATTERN_CONTENTS_LIMIT = 10;

const HormonePatternContents = () => {
  const { t } = useTranslation();
  const { offset, orderBy, orderDir, setOffset, setOrderBy, setOrderDir } =
    usePaginationQueryParams();

  const query = queryString.stringify({
    limit: ADMIN_HORMONE_PATTERN_CONTENTS_LIMIT,
    offset,
    order_by: isEmpty(orderBy) ? undefined : orderBy,
    order_dir: isEmpty(orderDir) ? undefined : orderDir,
  });

  const {
    data: paginatedHormonePatternContents,
    isPending: isGetHormonePatternContentsPending,
    isFetching: isGetHormonePatternContentsFetching,
    error: getHormonePatternContentsError,
  } = useQuery({
    queryKey: ['admin-hormone-pattern-contents', query],
    queryFn: () => loadHormonePatternContents(query),
    retry: 3,
  });

  const hormonePatternContents = paginatedHormonePatternContents?.docs ?? [];
  const total = paginatedHormonePatternContents?.total ?? 0;

  useEffect(() => {
    if (!orderBy && !orderDir) {
      setOffset(0);
      setOrderBy('name');
      setOrderDir('asc');
    }
  }, [setOrderDir, setOrderBy, setOffset, orderBy, orderDir]);

  const isLoading =
    isGetHormonePatternContentsPending || isGetHormonePatternContentsFetching;

  const goToPage = (_: any, value: number) => {
    setOffset((value - 1) * ADMIN_HORMONE_PATTERN_CONTENTS_LIMIT);
  };

  return (
    <Container>
      <CenterPane>
        <HeaderItems>
          <OvPageTitle
            title={t('common.hormonePatternContens')}
            icon={<StyledTocIcon />}
          />
        </HeaderItems>
        <ActionBar>
          <OvPaginationBar
            total={total}
            limit={ADMIN_HORMONE_PATTERN_CONTENTS_LIMIT}
            offset={offset}
            onChange={goToPage}
          />
        </ActionBar>

        {getHormonePatternContentsError && (
          <ErrorMessageWrapper>
            <OvErrorMessage message={getHormonePatternContentsError?.message} />
          </ErrorMessageWrapper>
        )}

        {hormonePatternContents?.length ? (
          <ContentWrapper>
            <OvHormonePatternContentsList
              hormonePatternContents={hormonePatternContents}
            />
          </ContentWrapper>
        ) : !isLoading ? (
          <OvNoContent>{t('common.empty')}</OvNoContent>
        ) : null}

        {isLoading && <OvLoadingIndicator position="fixed" />}
      </CenterPane>
    </Container>
  );
};

export default HormonePatternContents;

const Container = styled.div`
  display: flex;
`;

const CenterPane = styled.div`
  flex-grow: 1;
  margin: 0 auto;
  padding: 0 1rem;
  margin-left: 1rem;
  background-color: ${Colours.WHITE};
`;

const HeaderItems = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  justify-content: flex-start;
  gap: 1rem;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const StyledTocIcon = styled(TocIcon)`
  && {
    margin-right: 0.5rem;
  }
`;

const ErrorMessageWrapper = styled.div`
  margin: 1rem 0;
`;

const ContentWrapper = styled.div`
  margin-top: 1.5rem;
`;
